import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useActiveTeam } from "@/hooks/use-team";
import { api } from "@/lib/api";
import { homePathFor, spaceFeatures, type Feature } from "@/lib/access";

export type { Feature, MyAccess } from "@/lib/access";

/**
 * What the signed-in user may open (GET /api/me/access). Access is separate
 * per space, so this follows the space chosen in the top bar switcher: the
 * personal space, or the active team (see spaceFeatures). This only decides
 * what to show; the API checks every request on its own.
 */
export function useAccess() {
	const { user, isLoading: authLoading } = useAuth();
	const { activeTeam, isLoading: teamLoading } = useActiveTeam();
	const query = useQuery({
		queryKey: ["my-access", user?.id],
		enabled: !!user?.id,
		queryFn: () => api.getMyAccess(),
		staleTime: 60_000,
		// A Super Admin may change someone's access while they work.
		refetchOnWindowFocus: true,
	});

	const { current, anySpace } = spaceFeatures(query.data, activeTeam?.id ?? null);
	const has = (feature: Feature) => current.includes(feature);

	return {
		isLoading: authLoading || (!!user?.id && (query.isPending || teamLoading)),
		isSuperAdmin: query.data?.siteRole === "super_admin",
		/** In the space chosen in the switcher (DOOH: the personal space). */
		has,
		hasAny: (...wanted: Feature[]) => wanted.some(has),
		/** In at least one space: for pages opened by id, whose own space the API checks. */
		hasAnySpace: (...wanted: Feature[]) => wanted.some((feature) => anySpace.has(feature)),
		/** The first page this user can open in the current space. */
		homePath: homePathFor(current),
	};
}
